import User from '@this/domain/user/user';
import _ from 'lodash';
import type ExpensesApproverCandidate from '@this/domain/department/expenses_approver_candidate';
import type { InvoiceSplitTargetArgs } from '@this/domain/invoice/invoice_split_target';
import type { OrganizationAddressArgs } from '@this/domain/department/organization_address/organization_address';
import type { WorkflowStyle } from '@this/domain/workflow_style';
import type { workflowStyle } from '@this/domain/organization/organization2';
import type OrganizationMemberJson from '@this/domain/user/organization_member_json';
import type { ApprovalAuthorityResponseArgs } from '@this/domain/approval_authority/approval_authority';
import type ApprovalAuthority from '@this/domain/approval_authority/approval_authority';
import { convertApprovalAuthorityResponseToArgs } from '@this/domain/approval_authority/approval_authority';
import type { WorkflowType } from '@this/domain/workflow/workflow';
import OrganizationAddress from '../organization_address';

export type ApproverIdType = '' | number;
// ex) { default: { 1: [''] }, overseas: { 1: [''] } }
export type ApproveStagesType = Record<WorkflowType, Record<number, ApproverIdType[]>>;

// ビズトラ旧ワークフロー（経理承認）の承認者情報
export type ExpensesApprovalAuthorityArgs = {
  expenses_approver_id: number;
};

export type DepartmentArgs = {
  id?: number;
  name?: string;
  code?: string;
  organization_address?: OrganizationAddressArgs;
  monthly_budget?: number | null;
  disabled?: boolean;
  approval_authorities?: ApprovalAuthorityResponseArgs[];
  expenses_approval_authorities?: ExpensesApprovalAuthorityArgs[];
  expenses_approver_candidates?: ExpensesApproverCandidate[];
  freee_section_id?: number | null;
  freee_section_name?: string | null;
  workflow_style?: WorkflowStyle | null;
  invoice_split_targets?: InvoiceSplitTargetArgs[];
  approvers?: OrganizationMemberJson[];
};

// ビズトラ旧ワークフロー（経理承認）の承認者情報
export interface ExpensesApprovalAuthority {
  id?: number;
  department_id?: number;
  expenses_approver_id: number;
  created_at?: string;
  updated_at?: string;
  // 「承認者を削除」を押した場合: '1', 押していない場合: undefined
  destroy?: string;
}

export default class Department {
  readonly id: number | undefined;

  name: string | undefined;

  code: string | undefined;

  readonly organizationAddress: OrganizationAddress;

  address: string;

  postalCode: string;

  addressee: string;

  monthlyBudget: number | '';

  disabled: boolean | undefined;

  approveStages: ApproveStagesType;

  readonly expensesApprovalAuthorities: ExpensesApprovalAuthority[];

  readonly expensesApproverCandidates: ExpensesApproverCandidate[];

  freeeSectionId: number | null | undefined;

  freeeSectionName: string | null | undefined;

  workflowStyle: WorkflowStyle | null;

  invoiceSplitTargetId: number | null;

  readonly members: User[] | undefined;

  approvers: User[];

  approvalAuthorities: ApprovalAuthority[];

  constructor(args: DepartmentArgs, members?: User[]) {
    this.id = args.id;
    this.name = args.name;
    this.code = args.code;
    this.organizationAddress = args.organization_address
      ? new OrganizationAddress(args.organization_address)
      : new OrganizationAddress({});
    this.address = this.organizationAddress.location || '';
    this.postalCode = this.organizationAddress.postalCode || '';
    this.addressee = this.organizationAddress.addressee || '';
    this.monthlyBudget = args.monthly_budget || '';
    this.disabled = args.disabled;
    this.approveStages = (args.approval_authorities || []).reduce(
      (approveStages: ApproveStagesType, authority: ApprovalAuthorityResponseArgs) => {
        const workflowType = authority.workflow ? authority.workflow.workflow_type : 'default';
        const stage = authority.approve_stage;
        const id = authority.approver_id || '';

        if (approveStages[workflowType][stage] != null && approveStages[workflowType][stage][0] !== '') {
          approveStages[workflowType][stage].push(id);
        } else {
          approveStages[workflowType][stage] = [id];
        }
        return approveStages;
      },
      { default: { 1: [''] }, overseas: { 1: [''] } }
    );
    this.expensesApprovalAuthorities = args.expenses_approval_authorities || [];
    this.expensesApproverCandidates = args.expenses_approver_candidates || [];
    this.freeeSectionId = args.freee_section_id;
    this.freeeSectionName = args.freee_section_name;
    this.workflowStyle = args.workflow_style || null;
    this.invoiceSplitTargetId =
      (args.invoice_split_targets && args.invoice_split_targets[0] != null && args.invoice_split_targets[0].id) ||
      null;
    this.members = members;
    this.approvers = _.map(args.approvers, a => new User(a)) || [];
    this.approvalAuthorities = _.map(args.approval_authorities, a => convertApprovalAuthorityResponseToArgs(a));
  }

  membersNotApprover(workflowType: WorkflowType, stage: number) {
    return _.filter(this.members, m => {
      return !_.includes(this.approveStages[workflowType][stage], m.id);
    });
  }

  membersNotAdmin() {
    return _.filter(this.members, m => !m.isOrganizationAdmin);
  }

  approversFromIds(ids: number[]) {
    const approvers = _.map(ids, id => _.find(this.members, m => m.id === id));
    return _.compact(approvers);
  }

  approverNames(approveStages: Record<number, ApproverIdType[]>, step: string) {
    // 対象の承認ステップ
    const approveStage: ApproverIdType[] = approveStages[Number(step)];
    // 空文字を削除
    const filteredIds: number[] = _.filter(approveStage, as => as !== '') as number[];

    return _.map(this.approversFromIds(filteredIds), a => a.name);
  }

  expensesApprovalAuthoritiesToShow() {
    return _.filter(this.expensesApprovalAuthorities, auth => !auth.destroy);
  }

  expensesApprovers() {
    const approvers = _.map(this.expensesApprovalAuthoritiesToShow(), auth => {
      return _.find(this.members, m => m.id === auth.expenses_approver_id);
    });
    return _.compact(approvers);
  }

  addExpensesApprovalAuthority() {
    const members = this.membersNotAdmin();
    if (members && members.length > 0) {
      const newAuth: ExpensesApprovalAuthority = { expenses_approver_id: members[0].id };
      this.expensesApprovalAuthorities.push(newAuth);
    }
    return app.render();
  }

  removeExpensesApprovalAuthority(authority: ExpensesApprovalAuthority) {
    authority.destroy = '1';
    return app.render();
  }

  setExpensesApprover(authority: ExpensesApprovalAuthority, approver_id: number) {
    authority.expenses_approver_id = approver_id;
    return app.render();
  }

  setCode(value: string) {
    this.code = value;
    return app.render();
  }

  setName(value: string) {
    this.name = value;
    return app.render();
  }

  // idsがnullやundefinedになることは無さそうだったため|| []を削除
  setApproverIds(ids: number[], s: number, workflowType: WorkflowType) {
    this.approveStages[workflowType][s] = ids;
    return app.render();
  }

  // approveStagesがnullやundefinedになることは無さそうだったため
  // || { default: { 1: [''] }, overseas: { 1: [''] } }を削除
  setApproveStages(approveStages: ApproveStagesType) {
    this.approveStages = approveStages;
    return app.render();
  }

  setApproveStageParam(multipleApproveStages: ApproveStagesType) {
    return _.mapValues(multipleApproveStages, approveStages =>
      _.mapValues(approveStages, as => _.filter(as, a => a !== ''))
    );
  }

  setPostalCode(value: string) {
    this.postalCode = value;
    return app.render();
  }

  setAddress(value: string) {
    this.address = value;
    return app.render();
  }

  setAddressee(value: string) {
    this.addressee = value;
    return app.render();
  }

  setMonthlyBudget(value: number) {
    this.monthlyBudget = value;
    return app.render();
  }

  setFreeeSection(value: { id: number | null | undefined; name: string | null | undefined } | null) {
    this.freeeSectionId = value?.id || null;
    this.freeeSectionName = value?.name || null;
    return app.render();
  }

  setDisabled(value: boolean) {
    this.disabled = value;
    return app.render();
  }

  // value: "" | "department" | "trip"
  setWorkflowStyle(value: workflowStyle | '') {
    this.workflowStyle = value || null;
    return app.render();
  }

  setInvoiceSplitTargetId(value: number | null) {
    this.invoiceSplitTargetId = value || null;
    return app.render();
  }

  updateParams() {
    return {
      name: this.name,
      code: this.code,
      organization_address_attributes: {
        location: this.address,
        postal_code: this.postalCode,
        addressee: this.addressee
      },
      monthly_budget: this.monthlyBudget,
      disabled: this.disabled,
      approve_stages: this.setApproveStageParam(this.approveStages),
      expenses_approval_authorities_attributes: this.expensesApprovalAuthorities,
      freee_section_id: this.freeeSectionId,
      freee_section_name: this.freeeSectionName,
      workflow_style: this.workflowStyle,
      invoice_split_target_id: this.invoiceSplitTargetId
    };
  }

  noAddress() {
    return _.isEmpty(this.address);
  }

  description() {
    if (this.noAddress()) {
      return `${this.name}:住所が登録されていません`;
    }
    return `${this.name}:${this.postalCode} ${this.address} ${this.addressee}`;
  }

  descriptionWithCode() {
    return `${this.code}:${this.name}`;
  }
}
